import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import NotFound from "@/pages/NotFound";
import { works } from "@/mocks/works";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FAV_KEY = "mango_media_favorites";

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((w) => w.id === id);

  const [favorites, setFavorites] = useState<string[]>([]);
  const [toast, setToast] = useState("");

  useDocumentTitle(
    work ? `${work.title} - 芒果传媒案例展示` : "作品详情 - 芒果传媒",
    work ? `${work.summary} ${work.title} 由芒果传媒出品，${work.type}，${work.year} 年制作。` : undefined
  );

  useEffect(() => {
    setFavorites(readFavorites());
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const related = useMemo(() => {
    if (!work) return [];
    return works
      .filter((w) => w.id !== work.id && (w.type === work.type || w.year === work.year))
      .slice(0, 3);
  }, [work]);

  if (!work) return <NotFound />;

  const isFav = favorites.includes(work.id);

  const toggleFav = () => {
    const next = isFav ? favorites.filter((f) => f !== work.id) : [...favorites, work.id];
    setFavorites(next);
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
    setToast(isFav ? "已取消收藏" : "已加入收藏");
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("作品链接已复制");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="bg-background-50">
      <section className="bg-secondary-50 pt-28 md:pt-32 pb-10 md:pb-14">
        <div className="container-site">
          <Breadcrumb items={[{ label: "作品案例", path: "/works" }, { label: work.title }]} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mt-8">
            <div className="lg:col-span-5">
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden">
                <img
                  src={work.cover}
                  alt={work.title}
                  title={`${work.title} - 芒果传媒案例展示`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-7 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 w-fit px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                <i className="ri-movie-2-line"></i>
                {work.type}
              </span>
              <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 mt-4">
                {work.title}
              </h1>
              <p className="text-foreground-700 mt-4 leading-relaxed text-sm md:text-base">
                {work.summary}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-7">
                <div className="bg-background-50 rounded-lg p-4 border border-background-200/80">
                  <span className="text-xs text-foreground-500">拍摄年份</span>
                  <p className="font-heading font-bold text-foreground-950 mt-1">{work.year}</p>
                </div>
                <div className="bg-background-50 rounded-lg p-4 border border-background-200/80">
                  <span className="text-xs text-foreground-500">导演</span>
                  <p className="font-heading font-bold text-foreground-950 mt-1">{work.director}</p>
                </div>
                <div className="bg-background-50 rounded-lg p-4 border border-background-200/80">
                  <span className="text-xs text-foreground-500">片长 / 规格</span>
                  <p className="font-heading font-bold text-foreground-950 mt-1 text-sm">{work.duration}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-7">
                <button
                  type="button"
                  onClick={toggleFav}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    isFav
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-primary-500 text-foreground-950 hover:bg-primary-600"
                  }`}
                >
                  <i className={isFav ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {isFav ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={share}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
              项目介绍
            </h2>
            <div className="flex flex-col gap-4 mt-5">
              {work.description.map((p, i) => (
                <p key={i} className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  {p}
                </p>
              ))}
            </div>

            <div className="flex flex-col gap-5 mt-8">
              {work.gallery.map((g, i) => (
                <div key={i} className="w-full aspect-[12/7] rounded-lg overflow-hidden">
                  <img
                    src={g}
                    alt={`${work.title} 剧照 ${i + 1}`}
                    title={`${work.title} - 芒果传媒作品剧照`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="bg-secondary-50 rounded-lg p-6 border border-background-200/80 lg:sticky lg:top-28">
              <h3 className="font-heading font-bold text-lg text-foreground-950">项目亮点</h3>
              <ul className="flex flex-col gap-3 mt-4">
                {work.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill text-primary-600 mt-0.5"></i>
                    <span className="leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-background-200 mt-5 pt-5">
                <span className="text-xs text-foreground-500">出品 / 制作</span>
                <p className="font-medium text-foreground-900 mt-1 text-sm">{work.producer}</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-secondary-50 py-14 md:py-20">
          <div className="container-site">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
                相关作品推荐
              </h2>
              <Link
                to="/works"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:gap-3 transition-all whitespace-nowrap cursor-pointer"
              >
                全部作品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {related.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="container-site py-10">
        <Link
          to="/works"
          className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回作品案例列表
        </Link>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-medium shadow-lg animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}